import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '~/api/client';

/**
 * Minimal async data loading.
 *
 * A data-fetching library is not warranted here: the marketplace's reads are either cached hard at
 * the edge or explicitly user-scoped and uncacheable, so a client-side cache would mostly duplicate
 * what the API already decided. What is genuinely needed is cancellation on unmount and on changed
 * inputs — without it, a fast filter change lands two responses in whichever order the network
 * chose, and the older one wins.
 */

export interface QueryState<T> {
  readonly data: T | undefined;
  readonly error: ApiError | undefined;
  readonly isLoading: boolean;
  /** True on a background refresh, so a list can dim rather than flash to a spinner. */
  readonly isRefreshing: boolean;
  reload(): void;
}

export function useQuery<T>(
  load: (signal: AbortSignal) => Promise<T>,
  dependencies: readonly unknown[],
  options: { enabled?: boolean } = {},
): QueryState<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(enabled);
  const [reloadToken, setReloadToken] = useState(0);
  const hasData = data !== undefined;

  // Held in a ref so a changing inline closure does not itself retrigger the effect; the caller's
  // dependency list is the sole trigger.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(undefined);

    loadRef
      .current(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setIsLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(toApiError(cause));
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, enabled, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return { data, error, isLoading, isRefreshing: isLoading && hasData, reload };
}

export type MutationOutcome<TOutput> =
  | { readonly ok: true; readonly data: TOutput }
  | { readonly ok: false; readonly error: ApiError };

export interface MutationState<TInput, TOutput> {
  run(input: TInput): Promise<MutationOutcome<TOutput>>;
  readonly isRunning: boolean;
  readonly error: ApiError | undefined;
  clearError(): void;
}

/**
 * A single in-flight write with its own error state.
 *
 * `run` resolves rather than rejecting, because almost every caller wants "did it work?" and not a
 * try/catch around a click handler. It resolves to a tagged outcome rather than to a nullable value
 * specifically so that a mutation returning nothing — a rename, a delete — is still distinguishable
 * from one that failed. The error is also kept in state, including the per-field messages the API
 * returns, so a form can render it without holding it itself.
 */
export function useMutation<TInput, TOutput>(
  perform: (input: TInput) => Promise<TOutput>,
): MutationState<TInput, TOutput> {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const run = useCallback(
    async (input: TInput): Promise<MutationOutcome<TOutput>> => {
      setIsRunning(true);
      setError(undefined);
      try {
        const data = await perform(input);
        if (mounted.current) setIsRunning(false);
        return { ok: true, data };
      } catch (cause) {
        const error = toApiError(cause);
        if (mounted.current) {
          setError(error);
          setIsRunning(false);
        }
        return { ok: false, error };
      }
    },
    [perform],
  );

  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  return { run, isRunning, error, clearError };
}

/**
 * Polls until `isDone` says to stop.
 *
 * Used for watching an installation, which runs on a queue and can take a minute. The interval
 * backs off so a stuck installation does not turn into a permanent request loop, and polling stops
 * entirely once the resource reaches a terminal state.
 */
export function usePolling(
  tick: () => void,
  options: { active: boolean; intervalMs: number; maxIntervalMs?: number },
): void {
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (!options.active) return;

    let cancelled = false;
    let delay = options.intervalMs;
    const ceiling = options.maxIntervalMs ?? options.intervalMs * 6;
    let timer = 0;

    const schedule = (): void => {
      timer = window.setTimeout(() => {
        if (cancelled) return;
        tickRef.current();
        delay = Math.min(Math.round(delay * 1.35), ceiling);
        schedule();
      }, delay);
    };
    schedule();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [options.active, options.intervalMs, options.maxIntervalMs]);
}

function toApiError(cause: unknown): ApiError {
  if (cause instanceof ApiError) return cause;
  return new ApiError({
    status: 0,
    code: 'UNKNOWN',
    message: 'Something went wrong. Please try again.',
  });
}
